import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { CancelledError, useQuery, useQueryClient } from 'react-query';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchItemsFilters,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsSort,
  FetchQueryCacheCallback,
  FetchQueryCacheEvent,
  FetchQueryCacheEventType,
  NanoID,
  UUID
} from '../../../../../types';

import { IndexQueryResponse } from '../useIndexQuery';
import {
  indexRequestReducer,
  IndexRequestReducerType
} from '../useIndexQuery/reducers/indexRequestReducer';
import {
  CacheIndexQueryErrorType,
  CacheIndexQueryOptions
} from './useCacheIndexQuery.types';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useUpdateIndexQueryItemCache } from '../useIndexQuery/hooks/useUpdateIndexQueryItemCache';
import { useUpdateIndexQueryItemsCache } from '../useIndexQuery/hooks/useUpdateIndexQueryItemsCache';

import { changeItemsFiltersAction } from '../useIndexQuery/actions/changeItemsFiltersAction';
import { clearItemsFiltersAction } from '../useIndexQuery/actions/clearItemsFiltersAction';
import { filterItemsAction } from '../useIndexQuery/actions/filterItemsAction';
import { limitItemsAction } from '../useIndexQuery/actions/limitItemsAction';
import { paginateItemsAction } from '../useIndexQuery/actions/paginateItemsAction';
import { sortItemsAction } from '../useIndexQuery/actions/sortItemsAction';
import { dispatchUpdateState } from '../useIndexQuery/actions/updateStateAction';

import { handleFetchCacheItems } from './utils/handleFetchCacheItems';
import { handleFetchCacheItem } from './utils/handleFetchCacheItem';
import { LocalForage } from '../../../../../utils/LocalForage';
import { parseRequestError } from '../../../../../utils/parseRequestError';

import {
  INITIAL_FILTERS,
  INITIAL_LIMIT,
  INITIAL_PAGE,
  INITIAL_SORT
} from '../useIndexQuery/indexRequestConstants';

import { CommonPermissions } from '../../../commonConstants';
import { buildCacheKey } from '../../../../../utils/buildCacheKey';

function useCacheIndexQuery<
  NodeType,
  MetaType = undefined,
  AggregationsType = undefined,
  ExtraType = undefined
>({
  action,
  additionalParams,
  cacheKey,
  defaultApi = 'read_api',
  fetchCacheItemSource = FetchCacheItemSources.MNESIA,
  fetchItemCacheKey,
  fetchItemQuery,
  fetchCacheItemQuery,
  initialFilters = INITIAL_FILTERS,
  initialLimit = INITIAL_LIMIT,
  initialPage = INITIAL_PAGE,
  initialSort = INITIAL_SORT,
  onInvalidate,
  options = {},
  query,
  cacheQuery,
  scope,
  source = FetchCacheItemsSources.ES,
  trackTotalHits = false
}: CacheIndexQueryOptions<NodeType, MetaType, AggregationsType, ExtraType>) {
  const currentUser = useCurrentUser();

  const invalidationListenersRef = useRef<Set<FetchQueryCacheCallback>>(
    new Set()
  );

  useEffect(() => {
    if (onInvalidate) {
      invalidationListenersRef.current.add(onInvalidate);

      return () => {
        invalidationListenersRef.current.delete(onInvalidate);
      };
    }
  }, [onInvalidate]);

  const notifyInvalidation = useCallback(
    (queryKey: unknown[], type: FetchQueryCacheEventType) => {
      const event: FetchQueryCacheEvent = {
        queryKey,
        type
      };

      invalidationListenersRef.current.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          console.error('Error in invalidation listener:', error);
        }
      });
    },
    []
  );

  const localForageCacheKey = `${cacheKey}-index`;

  const targetApi =
    currentUser?.hasPermissions(action) &&
    currentUser.hasPermissions(CommonPermissions.READ_CACHE_QUERY) &&
    cacheQuery
      ? 'cache_api'
      : defaultApi;

  const currentQuery = targetApi === 'cache_api' ? cacheQuery : query;

  const currentFetchItemQuery =
    targetApi === 'cache_api' ? fetchCacheItemQuery : fetchItemQuery;

  const [{ currentFilters, currentLimit, currentPage, currentSort }, dispatch] =
    useReducer<IndexRequestReducerType>(indexRequestReducer, {
      currentPage: initialPage,
      currentLimit: initialLimit,
      currentFilters: initialFilters,
      currentSort: initialSort
    });

  const { data: placeholderData, isFetched: placeholderDataFetched } =
    useQuery<IndexQueryResponse<NodeType, MetaType, AggregationsType> | null>(
      `${cacheKey}-placeholder`,
      () =>
        LocalForage.getItem<
          IndexQueryResponse<NodeType, MetaType, AggregationsType>
        >(localForageCacheKey),
      { enabled: options.enabledPlaceholder }
    );

  const currentParams = useMemo(
    () => ({
      filters: currentFilters,
      sort: currentSort,
      page: currentPage,
      limit: currentLimit,
      ...additionalParams
    }),
    [currentFilters, currentSort, currentPage, currentLimit, additionalParams]
  );

  const fullCacheKey = useMemo(
    () => buildCacheKey(cacheKey, currentParams),
    [cacheKey, currentParams]
  );

  const { data, isFetched, isLoading, error, isPlaceholderData } = useQuery<
    IndexQueryResponse<NodeType, MetaType, AggregationsType, ExtraType>,
    CacheIndexQueryErrorType
  >(
    fullCacheKey,
    () =>
      handleFetchCacheItems({
        query: currentQuery,
        source,
        targetApi,
        trackTotalHits,
        ...currentParams
      }),
    {
      enabled:
        options.enabled === false
          ? false
          : options.enabled ||
            (options.enabledPlaceholder && placeholderDataFetched),
      cacheTime: options.cacheTime,
      staleTime: options.staleTime,
      refetchInterval: options.refetchInterval,
      keepPreviousData: options.keepPreviousData,
      refetchIntervalInBackground: options.refetchIntervalInBackground,
      onSuccess: (data) => {
        options.onSuccess?.(data);
        if (
          isEqual(currentFilters, initialFilters) &&
          isEqual(currentSort, initialSort) &&
          currentPage === initialPage &&
          currentLimit === initialLimit
        ) {
          return LocalForage.setItem<
            IndexQueryResponse<NodeType, MetaType, AggregationsType, ExtraType>
          >(localForageCacheKey, data);
        }
      },
      placeholderData: () => {
        if (
          placeholderData &&
          isEqual(currentFilters, initialFilters) &&
          isEqual(currentSort, initialSort) &&
          currentPage === initialPage &&
          currentLimit === initialLimit
        ) {
          return placeholderData;
        }
      }
    }
  );

  const queryClient = useQueryClient();

  const queryResponseValue = data?.[scope];

  // Subscribe to React Query cache events
  useEffect(() => {
    const cache = queryClient.getQueryCache();

    let lastDataUpdateCount = 0;

    const unsubscribe = cache.subscribe((event) => {
      if (!event || !event.query) return;

      const eventQueryKey = event.query.queryKey;

      const isRelatedQuery =
        Array.isArray(eventQueryKey) &&
        eventQueryKey.length > 0 &&
        (eventQueryKey[0] === cacheKey ||
          eventQueryKey[0] === fullCacheKey ||
          JSON.stringify(eventQueryKey).includes(cacheKey));

      if (!isRelatedQuery) return;

      if (event.type === 'queryUpdated' && event.query.state.isFetching) {
        lastDataUpdateCount = event.query.state.dataUpdateCount + 1;
      }

      if (
        event.type === 'queryUpdated' &&
        event.query.state.isFetching === false &&
        lastDataUpdateCount !== event.query.state.dataUpdateCount
      ) {
        notifyInvalidation(
          eventQueryKey,
          event.query.state.isInvalidated
            ? FetchQueryCacheEventType.INVALIDATE
            : FetchQueryCacheEventType.UPDATE
        );
      } else if (event.type === 'queryRemoved') {
        notifyInvalidation(eventQueryKey, FetchQueryCacheEventType.REMOVE);
      } else if (event.type === 'queryUpdated') {
        notifyInvalidation(eventQueryKey, FetchQueryCacheEventType.ADD);
      }
    });

    return unsubscribe;
  }, [queryClient, cacheKey, fullCacheKey, notifyInvalidation]);

  useEffect(() => {
    if (options.withoutPrefetch || options.enabled === false) {
      return;
    }

    if (!queryResponseValue?.paginationInfo?.nextPage) {
      return;
    }

    const params = {
      filters: currentFilters,
      sort: currentSort,
      page: currentPage + 1,
      limit: currentLimit,
      ...additionalParams
    };

    queryClient.prefetchQuery([cacheKey, params], () =>
      handleFetchCacheItems({
        query: currentQuery,
        source,
        targetApi,
        trackTotalHits,
        ...params
      })
    );
  }, [
    cacheKey,
    queryClient,
    currentQuery,
    currentFilters,
    currentSort,
    currentPage,
    currentLimit,
    queryResponseValue?.paginationInfo?.nextPage,
    options.withoutPrefetch,
    options.enabled,
    additionalParams,
    targetApi,
    source,
    trackTotalHits
  ]);

  const nodesTotalCount = queryResponseValue?.nodes?.length
    ? queryResponseValue?.paginationInfo?.totalCount ||
      placeholderData?.[scope]?.paginationInfo?.totalCount ||
      0
    : null;

  const isLoadingTotalCount = isLoading
    ? placeholderData?.[scope]?.paginationInfo?.totalCount
    : null;

  const defaultTotalCount = 0;

  const itemsTotalCount =
    nodesTotalCount || isLoadingTotalCount || defaultTotalCount;

  const updateItemCache = useUpdateIndexQueryItemCache<NodeType>({
    fullCacheKey,
    scope
  });

  const updateItemsCache = useUpdateIndexQueryItemsCache<NodeType>({
    fullCacheKey
  });

  const prevFiltersRef = useRef({});

  const fetchData = useCallback(
    async (
      {
        filters,
        sort,
        page,
        limit,
        ...additionalParams
      }: {
        filters?: FetchItemsFilters;
        sort?: FetchItemsSort;
        page?: FetchItemsPage;
        limit?: FetchItemsLimit;
      },
      updateState = true
    ) => {
      const updatedFilters = { ...currentFilters, ...(filters || {}) };

      Object.keys(prevFiltersRef.current || {}).forEach((key) => {
        if (!(key in filters)) {
          delete updatedFilters[key];
        }
      });

      prevFiltersRef.current = structuredClone(filters);

      const updatedParams = {
        filters: updatedFilters,
        sort: isEmpty(sort) ? initialSort : sort,
        page: page || currentPage,
        limit: limit || currentLimit,
        ...additionalParams
      };

      updateState &&
        dispatchUpdateState(dispatch, updatedParams, currentParams);

      const newCacheKey = buildCacheKey(cacheKey, updatedParams);

      let result = null;

      try {
        result = await queryClient.fetchQuery<
          IndexQueryResponse<NodeType, MetaType, AggregationsType>
        >(
          newCacheKey,
          () =>
            handleFetchCacheItems({
              query: currentQuery,
              source,
              targetApi,
              trackTotalHits,
              ...updatedParams
            }),
          {
            cacheTime: options.cacheTime,
            staleTime: options.staleTime
          }
        );
      } catch (err) {
        if (err instanceof CancelledError) {
          return queryClient.getQueryData(fullCacheKey);
        }

        throw err;
      }

      return result;
    },
    [
      currentFilters,
      initialSort,
      currentPage,
      currentLimit,
      currentParams,
      cacheKey,
      queryClient,
      options.cacheTime,
      options.staleTime,
      currentQuery,
      source,
      targetApi,
      trackTotalHits,
      fullCacheKey
    ]
  );

  const prefetchData = useCallback(
    async ({
      filters,
      sort,
      page,
      limit,
      ...additionalParams
    }: {
      filters?: FetchItemsFilters;
      sort?: FetchItemsSort;
      page?: FetchItemsPage;
      limit?: FetchItemsLimit;
    }) => {
      const updatedFilters = { ...currentFilters, ...(filters || {}) };

      Object.keys(prevFiltersRef.current || {}).forEach((key) => {
        if (!(key in filters)) {
          delete updatedFilters[key];
        }
      });

      const updatedParams = {
        filters: updatedFilters,
        sort: isEmpty(sort) ? initialSort : sort,
        page: page || currentPage,
        limit: limit || currentLimit,
        ...additionalParams
      };

      const newCacheKey = buildCacheKey(cacheKey, updatedParams);

      await queryClient.prefetchQuery<
        IndexQueryResponse<NodeType, MetaType, AggregationsType>
      >(
        newCacheKey,
        () =>
          handleFetchCacheItems({
            query: currentQuery,
            source,
            targetApi,
            trackTotalHits,
            ...updatedParams
          }),
        {
          cacheTime: options.cacheTime,
          staleTime: options.staleTime
        }
      );
    },
    [
      currentFilters,
      initialSort,
      currentPage,
      currentLimit,
      cacheKey,
      queryClient,
      options.cacheTime,
      options.staleTime,
      currentQuery,
      source,
      targetApi,
      trackTotalHits
    ]
  );

  const fetchPreparedData = useCallback(
    async (
      params: {
        filters?: FetchItemsFilters;
        sort?: FetchItemsSort;
        page?: FetchItemsPage;
        limit?: FetchItemsLimit;
      },
      updateState = true
    ) => {
      let error = null;

      const result = await fetchData(params, updateState).catch((err) => {
        error = parseRequestError(err);
      });

      return {
        items: result?.[scope]?.nodes || <NodeType[]>[],
        itemsError: error,
        itemsTotalCount: result?.[scope]?.paginationInfo?.totalCount,
        aggregations: result?.[scope]?.aggregations || <AggregationsType>{},
        meta: result?.[scope]?.meta || <MetaType>{}
      };
    },
    [fetchData, scope]
  );

  const getCachedData = useCallback(async () => {
    const result =
      queryClient.getQueryData<
        IndexQueryResponse<NodeType, MetaType, AggregationsType>
      >(fullCacheKey);

    return {
      items: result?.[scope]?.nodes || <NodeType[]>[],
      itemsError: error,
      itemsTotalCount: result?.[scope]?.paginationInfo?.totalCount,
      aggregations: result?.[scope]?.aggregations || <AggregationsType>{},
      meta: result?.[scope]?.meta || <MetaType>{}
    };
  }, [error, fullCacheKey, queryClient, scope]);

  const subscribeInvalidationEvents = useCallback(
    (callback: FetchQueryCacheCallback) => {
      invalidationListenersRef.current.add(callback);

      return () => {
        invalidationListenersRef.current.delete(callback);
      };
    },
    []
  );

  const unsubscribeInvalidationEvents = useCallback(
    (callback: FetchQueryCacheCallback) => {
      invalidationListenersRef.current.delete(callback);
    },
    []
  );

  return {
    data,
    aggregations: queryResponseValue?.aggregations || <AggregationsType>{},
    items: queryResponseValue?.nodes || <NodeType[]>[],
    itemsError: parseRequestError(error),
    itemsErrorMessage: parseRequestError(error),
    itemsTotalCount,
    meta: queryResponseValue?.meta || <MetaType>{},
    isFetched,
    isLoading,
    fullCacheKey,
    isPlaceholderData,
    currentFilters,
    currentSort,
    currentPage,
    currentLimit,
    updateItemCache,
    updateItemsCache,
    subscribeInvalidationEvents,
    unsubscribeInvalidationEvents,
    fetchItems: useCallback(
      () => queryClient.fetchQuery(fullCacheKey),
      [queryClient, fullCacheKey]
    ),
    filterItems: useCallback(
      (nextFilters: FetchItemsFilters) =>
        dispatch(filterItemsAction(nextFilters)),
      [dispatch]
    ),
    changeItemsFilters: useCallback(
      (
        changedFilters: Partial<FetchItemsFilters>,
        removeFilters: string[] = []
      ) => dispatch(changeItemsFiltersAction(changedFilters, removeFilters)),
      [dispatch]
    ),
    clearItemsFiltersPersistInitial: useCallback(
      () => dispatch(filterItemsAction(initialFilters)),
      [dispatch, initialFilters]
    ),
    clearItemsFilters: useCallback(
      () => dispatch(clearItemsFiltersAction()),
      [dispatch]
    ),
    sortItems: useCallback(
      (nextSort: FetchItemsSort) => dispatch(sortItemsAction(nextSort)),
      [dispatch]
    ),
    paginateItems: useCallback(
      (nextPage: FetchItemsPage) => dispatch(paginateItemsAction(nextPage)),
      [dispatch]
    ),
    limitItems: useCallback(
      (nextLimit: FetchItemsLimit) => dispatch(limitItemsAction(nextLimit)),
      [dispatch]
    ),
    fetchData,
    fetchPreparedData,
    prefetchData,
    getCachedData,
    prefetchItems: useCallback(
      ({
        nextFilters,
        nextSort,
        nextPage,
        nextLimit
      }: {
        nextFilters?: FetchItemsFilters;
        nextSort?: FetchItemsSort;
        nextPage?: FetchItemsPage;
        nextLimit?: FetchItemsLimit;
      }) => {
        const params = {
          filters: nextFilters || currentFilters,
          sort: nextSort || currentSort,
          page: nextPage || currentPage,
          limit: nextLimit || currentLimit
        };
        return queryClient.prefetchQuery([cacheKey, params], () =>
          handleFetchCacheItems({
            query: currentQuery,
            source,
            targetApi,
            trackTotalHits,
            ...params
          })
        );
      },
      [
        currentFilters,
        currentSort,
        currentPage,
        currentLimit,
        queryClient,
        cacheKey,
        currentQuery,
        source,
        targetApi,
        trackTotalHits
      ]
    ),
    prefetchItem: useCallback(
      (itemUuid: UUID | NanoID) =>
        queryClient.prefetchQuery(
          [fetchItemCacheKey, itemUuid],
          () =>
            handleFetchCacheItem({
              query: currentFetchItemQuery,
              source: fetchCacheItemSource,
              targetApi,
              uuid: itemUuid
            }),
          { staleTime: 5000 }
        ),
      [
        queryClient,
        fetchItemCacheKey,
        currentFetchItemQuery,
        fetchCacheItemSource,
        targetApi
      ]
    )
  };
}

export default useCacheIndexQuery;
